import { Dialog, DialogContent, DialogTitle, Skeleton, cn } from "@strivelabs/ui-core";
import React, { useEffect, useState } from "react";

export type ImagePreview = { url: string; filename: string };

/**
 * Resolve an attachment click: images open in the in-app lightbox (when a
 * preview sink is provided); everything else opens in a new tab. Shared by the
 * chat composer, chat history, and the homepage composer so the open-vs-preview
 * policy lives in one place.
 */
export function openAttachment(
  attachment: { url?: string; mediaType?: string; filename: string },
  onPreview?: (preview: ImagePreview) => void
): void {
  const { url, mediaType, filename } = attachment;
  if (!url) return;
  if (mediaType?.startsWith("image/") && onPreview) {
    onPreview({ url, filename });
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

/**
 * Lightbox dialog for previewing an image attachment inline (instead of
 * opening a new browser tab). Shows a skeleton while the full-size image
 * loads. Shared by the chat composer, chat history, and the homepage composer.
 */
export const ImagePreviewDialog: React.FC<{
  preview: ImagePreview | null;
  onClose: () => void;
}> = ({ preview, onClose }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [preview?.url]);

  return (
    <Dialog
      open={!!preview}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-3xl border-none bg-transparent p-0 shadow-none">
        <DialogTitle className="sr-only">
          {preview?.filename ?? "Image preview"}
        </DialogTitle>
        {preview && (
          <div className="relative flex items-center justify-center">
            {!loaded && !error && (
              <Skeleton className="h-[60vh] w-full max-w-3xl rounded-lg" />
            )}
            {error ? (
              <div className="flex h-[40vh] w-full flex-col items-center justify-center rounded-lg bg-background p-6 text-center">
                <p className="text-sm font-medium text-destructive">
                  Failed to load image
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  The preview link may have expired — reopen the chat to refresh it.
                </p>
              </div>
            ) : (
              <img
                alt={preview.filename}
                className={cn(
                  "max-h-[80vh] max-w-full rounded-lg object-contain",
                  !loaded && "hidden"
                )}
                onLoad={() => setLoaded(true)}
                onError={() => setError(true)}
                src={preview.url}
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
